import { useState } from "react";
import useInfiniteScroll from "react-infinite-scroll-hook";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import homeIconBlue from "../../../assets/homeIconBlue.svg";
import { PropertyItemVM } from "@propertelligent/client-api";
import { useNavigate } from "react-router-dom";
import { PropertyService } from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import { URLS } from "../../../constants/constants";
import PropertyCardItem from "../../property/PropertyCardItem";
import { GetImages } from "../../../utils/GetImages";

interface DataHookState {
  rows: PropertyItemVM[];
  loading: boolean;
  hasMore: boolean;
  fetchData: (page: number) => void;
  replaceRow?: (row: PropertyItemVM) => void;
  currentPage: number;
}

interface PropertyProps {
  Id: string;
  IsFooter?: boolean;
  hasAnyProperties?: boolean;
  dataHookState: DataHookState;
}

const PropertyCard: React.FC<PropertyProps> = ({
  Id,
  IsFooter = true,
  hasAnyProperties = true,
  dataHookState,
}) => {
  const { rows, loading, hasMore, fetchData, replaceRow, currentPage } = dataHookState;
  const navigate = useNavigate();
  const [showConfirmation, setShowConfirmation] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadMoreRecords = () => {
    if (!loading && hasMore) {
      fetchData(currentPage + 1);
    }
  };

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage: hasMore,
    onLoadMore: loadMoreRecords,
    disabled: !hasMore,
    rootMargin: "0px 0px 100px 0px",
  });

  // Refresh only the card that changed, so its counts update even after more pages were loaded on scroll.
  const handlePropertyChanged = (propertyId: number) => {
    if (!replaceRow) {
      fetchData(1);
      return;
    }
    apiHelper(PropertyService.getApiProperty1, { showNotification: false }, propertyId)
      .then((res) => replaceRow(res?.data?.success))
      .catch((err) => console.error("Error refreshing property:", err));
  };

  const handleDeleteProperty = (propertyId: any) => {
    if (isDeleting) return;

    setIsDeleting(true);
    apiHelper(PropertyService.deleteApiProperty, {
      successMessage: "Property Deleted Successfully",
      showNotification: true,
    }, propertyId)
      .then((res) => {
      if (res?.data?.success) {
        // Re-fetch the data to ensure the deleted unit is removed
        fetchData(1);  // Reset list from page 1 so deleted row is not left in appended pages
        setShowConfirmation(null);
      }
    })
      .catch((err) => {
        console.error("Error deleting property:", err);
        setShowConfirmation(null);
      })
      .finally(() => {
        setIsDeleting(false);
      });
  };

  return (
    <div className="infinite-scroll-container">
      {rows.length === 0 && !loading ? (
        <EmptyState
          icon={GetImages.emptyStateProperty}
          heading={hasAnyProperties ? "No Properties Found" : "Add Your First Property"}
          buttonLabel={hasAnyProperties ? "Add Your Property" : "Add Property"}
          isButton={hasAnyProperties ? false : true}
          onButtonClick={() => navigate("/properties/create")}
        />
      ) : (
        <>
          {rows.map((listingData) => (
            <PropertyCardItem
              key={listingData.id}
              listingData={listingData}
              IsFooter={IsFooter}
              showConfirmation={showConfirmation}
              setShowConfirmation={setShowConfirmation}
              handleDeleteProperty={handleDeleteProperty}
              isDeletingProperty={isDeleting}
              onUnitAdded={() => handlePropertyChanged(listingData.id)}
            />
          ))}
          {(loading || hasMore) && (
            <div ref={sentryRef} className="loading-trigger">
              {loading && (
                <SimpleSpinner
                  myClassName={rows.length === 0 ? "Icon50" : "Icon40"}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PropertyCard;